/**
 * Resolution (spec 2026-10-08 §2): a repository's tool requests, under the
 * toolchain policy, become one `ToolchainPlan` (each tool baked or installed,
 * with the hosts its install needs, and every tool that could not be, with
 * why); the plan becomes the setup run's install commands; and the lock the
 * install writes decides which installed tools reach PATH.
 *
 * Per tool: pins beat constraints beat hints; among pins the first in the
 * readers' table order wins; a constraint takes the newest baked version that
 * fits (PHP the oldest, #2356: its lines are baked and legacy code breaks
 * forward), else the newest the backend offers for it, named as the version
 * prefix mise's `latest` matching resolves and pinned on the plan when the
 * lock is read back; a hint alone picks the newest hinted version. A
 * Terraform constraint is satisfied by the baked OpenTofu, with a note.
 */
import { QA_MISE_SETTINGS_PATH, QA_TOOLCHAIN, runtimeInstallCommand, toolchainEnv, toolchainLockDir, toolchainPath, toolchainPlanFromRuntimes, } from "./qaToolchain.js";
import { TOOL_POLICY, installHosts, isCoreTool, isLegacyRuntimeTool, overrideFor, refusedBackend, } from "./toolchainPolicy.js";
import { TOOL_VERSION_PATTERN, } from "./toolchainPlanTypes.js";
import { compareToolVersions, newestOfVersion, parseToolVersion, satisfiesRange } from "./toolchainVersion.js";
export const QA_BAKED_TOOLS = {
    node: Object.values(QA_TOOLCHAIN.node),
    python: Object.values(QA_TOOLCHAIN.python),
    ruby: Object.values(QA_TOOLCHAIN.ruby),
    go: Object.values(QA_TOOLCHAIN.go),
    dotnet: Object.values(QA_TOOLCHAIN.dotnet),
    php: Object.keys(QA_TOOLCHAIN.php),
    opentofu: [QA_TOOLCHAIN.opentofu],
    tflint: [QA_TOOLCHAIN.tflint],
    pnpm: [QA_TOOLCHAIN.pnpm],
    yarn: [QA_TOOLCHAIN.yarn1, QA_TOOLCHAIN.yarnBerry],
    composer: [QA_TOOLCHAIN.composer],
    uv: [QA_TOOLCHAIN.uv],
    poetry: [QA_TOOLCHAIN.poetry],
};
/** The home a plan's stored `path` and `env` are written for; each runner recomputes for its own. */
const DEFAULT_PLAN_HOME = "/home/user";
/** The version a spec names, for ordering: `temurin-21` orders as 21, `3.12` as 3.12.999. */
const versionKey = (spec) => newestOfVersion(spec.match(/\d+(?:\.\d+){0,2}/)?.[0] ?? "0.0.0");
const byNewest = (a, b) => compareToolVersions(versionKey(b), versionKey(a));
const isTerraform = (request) => request.source.detail?.startsWith("terraform") ?? false;
/**
 * The baked version a pin names: itself, the newest baked release of the
 * line it names (`22` → 22.23.3), or the baked line a patch pin belongs to
 * (PHP's lines are baked as lines: `8.1.2` → 8.1).
 */
function bakedForPin(spec, versions) {
    const exact = versions.find((version) => version === spec);
    if (exact)
        return exact;
    const sorted = [...versions].sort(byNewest);
    return (sorted.find((version) => version.startsWith(`${spec}.`)) ??
        sorted.find((version) => version.split(".").length === 2 && spec.startsWith(`${version}.`)) ??
        null);
}
/**
 * The version prefix an install asks mise for when no baked version meets
 * the ranges: the newest prefix the ranges name (and the majors between)
 * whose newest release they admit, so mise's `latest` matching installs the
 * newest release inside them. "latest" when they name no version at all.
 */
function installForRanges(ranges) {
    const named = ranges.flatMap((range) => [...range.matchAll(/\d+(?:\.\d+){0,2}/g)].map((match) => match[0]));
    if (!named.length)
        return "latest";
    const span = (values) => Array.from({ length: Math.min(Math.max(...values) - Math.min(...values) + 1, 100) }, (_, index) => Math.min(...values) + index);
    const majors = named.map((version) => Number(version.split(".")[0]));
    const between = [
        ...span(majors).map(String),
        // The minors between two a range names inside one major (`>=1.30 <1.32` → 1.31).
        ...[...new Set(majors)].flatMap((major) => {
            const minors = named.filter((version) => Number(version.split(".")[0]) === major && version.includes(".")).map((version) => Number(version.split(".")[1]));
            return minors.length ? span(minors).map((minor) => `${major}.${minor}`) : [];
        }),
    ];
    const candidates = [
        ...new Set([...named, ...between].flatMap((version) => {
            const parts = version.split(".");
            return parts.map((_, index) => parts.slice(0, index + 1).join("."));
        })),
    ];
    const admits = (version) => ranges.every((range) => satisfiesRange(newestOfVersion(version), range) !== false);
    const fit = candidates.filter(admits).sort(byNewest);
    if (fit.length)
        return fit[0];
    // Nothing open-ended fits (`=1.2.3`, `<1.4 >1.3.2`): the newest exact version they name that they admit.
    return named.filter((version) => version.split(".").length === 3 && ranges.every((range) => satisfiesRange(parseToolVersion(version), range) === true)).sort(byNewest)[0] ?? null;
}
/** The backend a tool installs through: its own prefix, core for a core tool, an override's, else aqua. */
function backendFor(name) {
    if (name.includes(":"))
        return name;
    if (isCoreTool(name))
        return "core";
    return overrideFor(name)?.backend ?? "aqua";
}
function choose(name, requests, baked) {
    // Terraform's versions name Terraform releases, not OpenTofu's: the baked
    // OpenTofu satisfies them, unless an OpenTofu version is named too.
    const own = name === "opentofu" ? requests.filter((request) => !isTerraform(request)) : requests;
    if (name === "opentofu" && !own.length && baked.length) {
        const tofu = [...baked].sort(byNewest)[0];
        const asked = [...new Set(requests.map((request) => `${request.spec} (${request.source.file})`))].join(", ");
        return {
            version: tofu,
            origin: "baked",
            spec: requests[0].spec,
            note: `OpenTofu ${tofu} runs this repository's Terraform configuration, which asks for Terraform ${asked}. HashiCorp's terraform is never installed.`,
        };
    }
    const pins = own.filter((request) => request.strength === "pin");
    const constraints = own.filter((request) => request.strength === "constraint");
    const hints = own.filter((request) => request.strength === "hint");
    if (pins.length) {
        const spec = pins[0].spec;
        const bakedVersion = bakedForPin(spec, baked);
        return bakedVersion ? { version: bakedVersion, origin: "baked", spec } : { version: spec, origin: "install", spec };
    }
    if (constraints.length) {
        const ranges = constraints.map((request) => request.spec);
        const spec = ranges.join(" ");
        const admits = (version) => {
            const parsed = parseToolVersion(version);
            return parsed !== null && ranges.every((range) => satisfiesRange(parsed, range) !== false);
        };
        // PHP's lines are baked and legacy code breaks forward: the oldest that fits.
        const order = name === "php" ? [...baked].sort(byNewest).reverse() : [...baked].sort(byNewest);
        const bakedVersion = order.find(admits);
        if (bakedVersion)
            return { version: bakedVersion, origin: "baked", spec };
        const install = installForRanges(ranges);
        return install ? { version: install, origin: "install", spec } : { unsatisfiable: { tool: name, spec, reason: "unverifiable" } };
    }
    if (hints.length) {
        const spec = [...hints.map((request) => request.spec)].sort(byNewest)[0];
        const bakedVersion = bakedForPin(spec, baked);
        return bakedVersion ? { version: bakedVersion, origin: "baked", spec } : { version: spec, origin: "install", spec };
    }
    return null;
}
/**
 * The plan for a repository's tool requests: every tool baked or installed
 * under `policy`, and every tool that cannot be, with its reason. `home` is the
 * run user's home the stored `path` and `env` are written for.
 */
export function resolveToolchain(requests, policy = TOOL_POLICY, baked = QA_BAKED_TOOLS, options = {}) {
    const tools = [];
    const unsatisfiable = [];
    const notes = [];
    const names = [...new Set(requests.map((request) => request.tool))];
    for (const name of names) {
        const own = requests.filter((request) => request.tool === name);
        const refused = refusedBackend(name, policy);
        if (refused) {
            unsatisfiable.push({ tool: name, spec: own[0].spec, reason: "backend_not_allowed" });
            continue;
        }
        const choice = choose(name, own, baked[name] ?? []);
        if (!choice)
            continue;
        if ("unsatisfiable" in choice) {
            unsatisfiable.push(choice.unsatisfiable);
            continue;
        }
        if (choice.note)
            notes.push(choice.note);
        const backend = backendFor(name);
        if (choice.origin === "baked") {
            tools.push({ name, version: choice.version, backend, origin: "baked", hosts: [] });
            continue;
        }
        const override = overrideFor(name);
        const floor = override?.floor ? parseToolVersion(override.floor) : null;
        const parsed = parseToolVersion(choice.version);
        let reason = null;
        if (override?.bakedOnly)
            reason = "not_baked";
        else if (override?.slow)
            reason = "install_too_slow";
        else if (floor && parsed && compareToolVersions(newestOfVersion(choice.version), floor) < 0)
            reason = "below_floor";
        else if (!TOOL_VERSION_PATTERN.test(choice.version))
            reason = "unverifiable";
        if (reason) {
            unsatisfiable.push({ tool: name, spec: choice.spec, reason });
            continue;
        }
        tools.push({ name, version: choice.version, backend, origin: "install", hosts: installHosts(name, backend) });
    }
    const plan = { tools, unsatisfiable, notes, path: "", env: {} };
    return withPlanEnvironment(plan, options.home ?? DEFAULT_PLAN_HOME);
}
function withPlanEnvironment(plan, home) {
    return { ...plan, path: toolchainPath(plan, { home }), env: toolchainEnv(plan, { home }) };
}
/**
 * The plan with its runtime lines taken from `runtimes`, preflight's choice,
 * which stays the source for them for one release (spec §6): each runtime
 * tool is replaced by the selection's, and a runtime the selection names is
 * never also unsatisfiable. Every other tool stands.
 */
export function withRuntimes(plan, runtimes, options = {}) {
    const selected = toolchainPlanFromRuntimes(runtimes).tools;
    const merged = {
        ...plan,
        tools: [...selected, ...plan.tools.filter((tool) => !isLegacyRuntimeTool(tool.name))],
        unsatisfiable: plan.unsatisfiable.filter((entry) => !(isLegacyRuntimeTool(entry.tool) && runtimes[entry.tool])),
    };
    return withPlanEnvironment(merged, options.home ?? DEFAULT_PLAN_HOME);
}
/** The agent PATH for a plan, under the run user's home. */
export function toolchainPlanPath(plan, home) {
    return toolchainPath(plan, { home });
}
/** The toolchain environment for a plan, under the run user's home. */
export function toolchainPlanEnv(plan, home) {
    return toolchainEnv(plan, { home });
}
/** The tools a plan installs through mise's own install (not a runtime's dedicated installer). */
export function miseInstalledTools(plan) {
    return plan.tools.filter((tool) => tool.origin === "install" && !isLegacyRuntimeTool(tool.name));
}
const shellQuote = (value) => `'${value.replaceAll("'", `'"'"'`)}'`;
function base64(text) {
    const bytes = new TextEncoder().encode(text);
    let binary = "";
    for (const byte of bytes)
        binary += String.fromCharCode(byte);
    return btoa(binary);
}
/**
 * The command that installs one mise tool of a plan, unprivileged, into the
 * run user's home: from `<MISE_DATA_DIR>/yagni-toolchain` (never the
 * checkout), with a generated `mise.toml` naming every tool the plan installs
 * through mise and a `mise.lock` beside it (the pinned mise writes a lock
 * only beside a config whose lock exists), under the baked settings, with
 * downloads on for this command only. With `lock`, the record's lock is
 * written first and `--locked` installs exactly the bytes it names. With
 * `timeoutSeconds`, coreutils' `timeout` caps it (exit 124).
 */
export function miseInstallCommand(tool, plan, home, options = {}) {
    const dir = toolchainLockDir(home);
    const config = ["[tools]", ...miseInstalledTools(plan).map((entry) => `"${entry.name}" = "${entry.version}"`)];
    const writeLock = options.lock
        ? `printf '%s' ${shellQuote(base64(options.lock))} | base64 -d > mise.lock`
        : "{ [ -f mise.lock ] || : > mise.lock; }";
    const cap = options.timeoutSeconds ? `timeout ${Math.max(1, Math.floor(options.timeoutSeconds))} ` : "";
    return (`mkdir -p ${dir} && cd ${dir} && printf '%s\\n' ${config.map(shellQuote).join(" ")} > mise.toml && ${writeLock} && ` +
        `${cap}env -u MISE_SHARED_INSTALL_DIRS MISE_DATA_DIR=${home}/.local/share/mise MISE_SYSTEM_CONFIG_FILE=${QA_MISE_SETTINGS_PATH} ` +
        `MISE_TRUSTED_CONFIG_PATHS=${dir} MISE_OFFLINE=0 MISE_YES=1 mise install ${options.lock ? "--locked " : ""}${tool.name}@${tool.version}`);
}
/**
 * Every install command a plan's setup runs, in order: each installed runtime
 * through its dedicated installer (Ruby's pinned precompiled build, .NET's
 * SHA-512-checked tarball, Python through uv, Go's toolchain module, Node
 * through mise), then each other tool through `miseInstallCommand`. Baked
 * tools install nothing; PHP never installs; a slow tool never reaches the
 * plan's tools.
 */
export function miseInstallCommands(plan, home, lock) {
    const runtimes = plan.tools.filter((tool) => tool.origin === "install" && isLegacyRuntimeTool(tool.name) && tool.name !== "php");
    return [
        ...runtimes.map((tool) => runtimeInstallCommand({ runtime: tool.name, version: tool.version, label: "" }, { home })),
        ...miseInstalledTools(plan)
            .filter((tool) => !overrideFor(tool.name)?.slow && !overrideFor(tool.name)?.bakedOnly)
            .map((tool) => miseInstallCommand(tool, plan, home, lock ? { lock } : {})),
    ];
}
/**
 * The entries of a mise.lock, read as the pinned mise writes it:
 * `[[tools.<name>]]` tables with `version` and `backend`, each followed by
 * `[tools.<name>."platforms.<platform>"]` tables with `checksum` and `url`.
 */
export function parseMiseLock(text) {
    const entries = [];
    let current = null;
    const unquote = (value) => value.replace(/^"|"$/g, "");
    for (const raw of text.split(/\r?\n/)) {
        const line = raw.trim();
        if (!line || line.startsWith("#"))
            continue;
        const tool = line.match(/^\[\[tools\.("[^"]+"|[^\].]+)\]\]$/);
        if (tool) {
            const entry = { name: unquote(tool[1]), version: "", backend: "", platforms: {} };
            entries.push(entry);
            current = { entry, platform: null };
            continue;
        }
        const platform = line.match(/^\[tools\.("[^"]+"|[^\].]+)\."platforms\.([^"]+)"\]$/);
        if (platform) {
            const name = unquote(platform[1]);
            const entry = [...entries].reverse().find((candidate) => candidate.name === name);
            current = entry ? { entry, platform: platform[2] } : null;
            if (entry)
                entry.platforms[platform[2]] ??= {};
            continue;
        }
        if (line.startsWith("[")) {
            current = null;
            continue;
        }
        const pair = line.match(/^([\w-]+)\s*=\s*"([^"]*)"$/);
        if (!pair || !current)
            continue;
        const [, key, value] = pair;
        if (current.platform === null) {
            if (key === "version")
                current.entry.version = value;
            if (key === "backend")
                current.entry.backend = value;
        }
        else {
            const target = current.entry.platforms[current.platform];
            if (key === "checksum")
                target.checksum = value;
            if (key === "url")
                target.url = value;
        }
    }
    return entries;
}
/** Whether a lock's exact version is the one a plan asked for (`temurin-21` → `temurin-21.0.12+101.0.LTS`). */
function lockVersionMatches(locked, asked) {
    return asked === "latest" || locked === asked || locked.startsWith(`${asked}.`) || locked.startsWith(`${asked}+`);
}
/**
 * The lock's verdict on a plan: every tool that stays (baked tools, runtimes
 * installed by their dedicated installers, and each mise-installed tool whose
 * lock entry came from an allowed backend and carries a checksum for
 * `platform`, pinned to the lock's exact version), and every mise-installed
 * tool that does not, as `unverifiable`. A tool missing from the lock is
 * unverifiable too: nothing proves what was downloaded.
 */
export function verifyLock(plan, lockText, options = {}) {
    const platform = options.platform ?? "linux-x64";
    const policy = options.policy ?? TOOL_POLICY;
    const lock = parseMiseLock(lockText);
    const verified = [];
    const unverifiable = [];
    for (const tool of plan.tools) {
        if (tool.origin !== "install" || isLegacyRuntimeTool(tool.name)) {
            verified.push(tool);
            continue;
        }
        const entry = lock.find((candidate) => candidate.name === tool.name && lockVersionMatches(candidate.version, tool.version));
        const backendType = entry?.backend.split(":")[0] ?? "";
        const checksum = entry?.platforms[platform]?.checksum ?? "";
        if (!entry || !policy.backends.includes(backendType)) {
            unverifiable.push({ tool: tool.name, spec: tool.version, reason: entry ? "backend_not_allowed" : "unverifiable" });
        }
        else if (policy.requireLockChecksum && !/^(?:sha256|sha512|blake3):[0-9a-f]{32,}$/.test(checksum)) {
            unverifiable.push({ tool: tool.name, spec: tool.version, reason: "unverifiable" });
        }
        else {
            verified.push(TOOL_VERSION_PATTERN.test(entry.version) ? { ...tool, version: entry.version, backend: entry.backend } : tool);
        }
    }
    return { verified, unverifiable };
}
/** The plan after its lock is read back: unverifiable tools off PATH and recorded, PATH and env recomputed. */
export function applyLockVerification(plan, lockText, home, options = {}) {
    const { verified, unverifiable } = verifyLock(plan, lockText, options);
    const next = withPlanEnvironment({ ...plan, tools: verified, unsatisfiable: [...plan.unsatisfiable, ...unverifiable] }, home);
    return { plan: next, unverifiable };
}
//# sourceMappingURL=toolchainPlan.js.map